/**
 * Scatter renderer for a single CustomPlant entry. Loads the uploaded GLB,
 * clones it `count` times at random positions on the terrain, and stashes
 * them as children of a group. No per-frame update — static props.
 *
 * For cheap scatter we clone the Object3D per instance; if the GLB has
 * non-skinned geometry we could upgrade to InstancedMesh later, but
 * cloning keeps arbitrary material/skinning support without extra logic.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Terrain } from '../game/Terrain';
import type { CustomPlant } from '../ui/WorldStore';

export class CustomPlantMesh {
  readonly group = new THREE.Group();
  /** Circular obstacles ants need to walk around. Populated during
   *  load() — empty until `ready` resolves. Each entry uses the plant's
   *  scaled XZ footprint, so ants treat the piaggio (or any uploaded
   *  prop) as a real solid object. */
  readonly obstacles: Array<{ x: number; z: number; radius: number }> = [];
  /** Resolves once the GLB has loaded and the scene + obstacles list
   *  have been populated. main.ts awaits all plant `ready`s before
   *  registering the combined obstacle list with AntWorld. */
  readonly ready: Promise<void>;
  private loaded = false;

  constructor(private cfg: CustomPlant, private terrain: Terrain, private worldRadius: number) {
    this.group.name = `custom-plant-${cfg.id}`;
    this.ready = this.load();
  }

  private async load(): Promise<void> {
    let gltf;
    try {
      gltf = await new GLTFLoader().loadAsync(this.cfg.glbUrl);
    } catch (err) {
      console.warn(`[CustomPlant ${this.cfg.id}] GLB load failed:`, err);
      return;
    }
    let src: THREE.Object3D = gltf.scene;
    const rotX = this.cfg.rotX ?? 0;
    const rotY = this.cfg.rotY ?? 0;
    const rotZ = this.cfg.rotZ ?? 0;
    // Seed RNG from the id so placement stays deterministic per plant.
    const rng = seededRng(hashString(this.cfg.id));

    // Two modes:
    //   - PACK mode (cfg.isPack === true): treat each mesh in the GLB as a
    //     variant and scatter INDIVIDUALLY, like RocksGLB. Use this for
    //     desert-stones / desert-shrubs etc.
    //   - SCENE mode (default): clone the whole GLB per instance. Use this
    //     for single objects built from multiple parts (trees, sculptures).
    if (this.cfg.isPack) {
      src.updateMatrixWorld(true);
      const variants: Array<{ geometry: THREE.BufferGeometry; material: THREE.Material | THREE.Material[] }> = [];
      src.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh || !m.geometry) return;
        const geo = m.geometry.clone();
        geo.applyMatrix4(m.matrixWorld);
        geo.computeBoundingBox();
        const bb = geo.boundingBox!;
        const sz = new THREE.Vector3(); bb.getSize(sz);
        if (sz.length() < 1e-4 || sz.length() > 80) return;
        const cx = (bb.min.x + bb.max.x) * 0.5;
        const cz = (bb.min.z + bb.max.z) * 0.5;
        geo.translate(-cx, -bb.min.y, -cz);
        variants.push({ geometry: geo, material: m.material });
      });
      if (variants.length === 0) { this.loaded = true; return; }
      for (let i = 0; i < this.cfg.count; i++) {
        const v = variants[Math.floor(rng() * variants.length)];
        const mat = Array.isArray(v.material) ? v.material[0] : v.material;
        const mesh = new THREE.Mesh(v.geometry, mat);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        const r = Math.sqrt(rng()) * this.worldRadius * 0.92;
        const a = rng() * Math.PI * 2;
        const x = Math.cos(a) * r;
        const z = Math.sin(a) * r;
        const y = this.terrain.getHeight(x, z);
        const s = this.cfg.scaleMin + rng() * Math.max(0, this.cfg.scaleMax - this.cfg.scaleMin);
        mesh.scale.setScalar(s);
        mesh.position.set(x, y + (this.cfg.offY ?? 0), z);
        mesh.rotation.set(rotX, rotY + rng() * Math.PI * 2, rotZ);
        if (this.cfg.alignTerrain) {
          const normal = this.terrain.getNormal(x, z, new THREE.Vector3());
          const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal);
          mesh.quaternion.premultiply(q);
        }
        this.group.add(mesh);
        // Obstacle radius — XZ half-extent of the variant's scaled bbox.
        // Use 0.7× the larger XZ half-extent so the collider sits inside
        // the silhouette rather than on top of foliage edges.
        const bb = v.geometry.boundingBox!;
        const halfX = (bb.max.x - bb.min.x) * 0.5;
        const halfZ = (bb.max.z - bb.min.z) * 0.5;
        const radius = Math.max(halfX, halfZ) * s * 0.7;
        if (radius > 0.05) this.obstacles.push({ x, z, radius });
      }
      this.loaded = true;
      return;
    }

    // SCENE mode — clone the whole GLB per instance. Heavy uploads
    // (e.g. an aircraft model) often pack hundreds of sub-meshes that
    // each become a draw call per instance. We collapse meshes by
    // material into a flat group, dropping the typical 200-mesh prop
    // to ~material-count meshes per clone. Falls back to the original
    // hierarchy on any merge failure (mismatched vertex attributes).
    //
    // CRITICAL: normalise the root transform to identity BEFORE merging
    // so the GLB's own root scale/rotation isn't baked into every
    // child's world matrix (which would inflate the merged bbox by the
    // root scale and produce phantom oversize obstacles).
    src.position.set(0, 0, 0);
    src.rotation.set(0, 0, 0);
    src.scale.setScalar(1);
    src.updateMatrixWorld(true);
    const merged = mergeBuiltScene(src);
    src = merged ?? src;
    src.rotation.set(rotX, rotY, rotZ);
    src.scale.setScalar(1);
    src.updateMatrixWorld(true);
    const bb = new THREE.Box3().setFromObject(src);
    const feetOff = -bb.min.y;

    // Pre-compute the source XZ half-extents from the unscaled scene
    // bbox so we can derive each clone's footprint without re-traversing.
    const sceneSize = new THREE.Vector3();
    bb.getSize(sceneSize);
    const halfXSrc = sceneSize.x * 0.5;
    const halfZSrc = sceneSize.z * 0.5;

    for (let i = 0; i < this.cfg.count; i++) {
      const clone = src.clone(true);
      clone.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.castShadow = true;
        m.receiveShadow = true;
      });
      const r = Math.sqrt(rng()) * this.worldRadius * 0.92;
      const a = rng() * Math.PI * 2;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      const y = this.terrain.getHeight(x, z);
      const s = this.cfg.scaleMin + rng() * Math.max(0, this.cfg.scaleMax - this.cfg.scaleMin);
      clone.scale.setScalar(s);
      clone.position.set(x, y + feetOff * s + (this.cfg.offY ?? 0), z);
      clone.rotation.y = rng() * Math.PI * 2;
      if (this.cfg.alignTerrain) {
        const normal = this.terrain.getNormal(x, z, new THREE.Vector3());
        const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal);
        clone.quaternion.premultiply(q);
      }
      this.group.add(clone);
      // Single circular obstacle covering the prop's silhouette. Cap at
      // a sane max so a misbehaving GLB (whose bbox we mis-measured)
      // doesn't drop a giant invisible cylinder onto the map.
      const radius = Math.min(4, Math.max(halfXSrc, halfZSrc) * s * 0.7);
      if (radius > 0.05) this.obstacles.push({ x, z, radius });
    }
    this.loaded = true;
  }

  dispose(): void {
    this.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.geometry?.dispose?.();
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      for (const mat of mats) mat?.dispose?.();
    });
    if (this.group.parent) this.group.parent.remove(this.group);
    void this.loaded;
  }
}

/**
 * Collapse a GLB scene's mesh hierarchy by material, returning a flat
 * Group containing one Mesh per material-group (geometries merged with
 * their world transforms baked in).
 *
 * Returns `null` if any merge fails — the caller falls back to the
 * original hierarchy. Skipped meshes (lines, points, missing geometry,
 * incompatible vertex attributes) just don't contribute to the output.
 */
function mergeBuiltScene(src: THREE.Object3D): THREE.Object3D | null {
  src.updateMatrixWorld(true);
  const groups = new Map<string, { geos: THREE.BufferGeometry[]; mat: THREE.Material }>();
  let meshCount = 0;
  let merged = false;
  src.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.geometry) return;
    meshCount++;
    const mat = (Array.isArray(m.material) ? m.material[0] : m.material) as THREE.Material;
    if (!mat) return;
    const key = mat.uuid;
    const geo = m.geometry.clone();
    geo.applyMatrix4(m.matrixWorld);
    const ent = groups.get(key) ?? { geos: [], mat };
    ent.geos.push(geo);
    groups.set(key, ent);
  });
  if (meshCount <= 1) return null;  // nothing to merge

  const out = new THREE.Group();
  for (const { geos, mat } of groups.values()) {
    if (geos.length === 0) continue;
    let m: THREE.BufferGeometry | null = null;
    try {
      m = (BufferGeometryUtils as { mergeGeometries?: (geos: THREE.BufferGeometry[], useGroups?: boolean) => THREE.BufferGeometry | null }).mergeGeometries
        ? (BufferGeometryUtils as { mergeGeometries: (geos: THREE.BufferGeometry[], useGroups?: boolean) => THREE.BufferGeometry | null }).mergeGeometries(geos, false)
        : null;
    } catch {
      m = null;
    }
    if (!m) {
      // Mixed attribute sets — bail and let caller use the original.
      return null;
    }
    out.add(new THREE.Mesh(m, mat));
    merged = true;
  }
  return merged ? out : null;
}

function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
  }
  return h >>> 0;
}

function seededRng(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
